import { ZipReader, BlobWriter, TextWriter } from '@zip.js/zip.js';
import { pptxUnitsToPixels } from './unitsUtils';
import type { CanvaFont, LayerBoundingBoxProperties, LayerElementsMetadata } from '@/typings/layers/layers';
import type {
    CustGeom,
    CustGeom2,
    FillRect,
    FillRect2,
    GradFill,
    GrpSp,
    GrpSp2,
    Pic,
    R,
    Sp,
    Sp2,
    SpTree,
    TextLine,
    Xfrm2
} from '@/typings/parsers/pptxParserTypes';
import * as OrderedSlide from '@/typings/parsers/pptxOrderedLayersTypes';
import { TreeType } from '@/components/Layers/modal/types';
import { useColor } from '@/composables/color';
import { ColorObject } from '@/typings/libraries';
import { createCanvas, canvasToBlob } from './canvasUtils';
import { getLoadedHtmlImage } from './imageUtils';
export type LayerNode = SpTree | GrpSp | GrpSp2 | Array<SpTree | GrpSp | GrpSp2>;
type TextAlign = 'l' | 'ctr' | 'r' | 'just';
type Slot = 'latin' | 'ea' | 'cs' | 'sym';
type OrderedPathChild =
    | { moveTo: any[] }
    | { lnTo: any[] }
    | { cubicBezTo: any[] }
    | { quadBezTo: any[] }
    | { close: any[] }
    | { ':@': Record<string, string> };

type OperationItem =
    | { type: 'moveTo'; item: any }
    | { type: 'lnTo'; item: any }
    | { type: 'cubicBezTo'; item: any }
    | { type: 'close' }
    | { type: 'attrs'; attrs: Record<string, string> };

const pptxToFabricLineCorrection = 0.53;
const SCRIPT_REGEXES: Record<Slot, RegExp> = {
    latin: /\p{Script=Latin}/u,
    ea: /\p{Script=Han}|\p{Script=Hiragana}|\p{Script=Katakana}|\p{Script=Hangul}/u,
    cs: /\p{Script=Arabic}|\p{Script=Hebrew}|\p{Script=Devanagari}|\p{Script=Bengali}|\p{Script=Gurmukhi}|\p{Script=Gujarati}|\p{Script=Oriya}|\p{Script=Tamil}|\p{Script=Telugu}|\p{Script=Kannada}|\p{Script=Malayalam}|\p{Script=Sinhala}|\p{Script=Thai}|\p{Script=Lao}|\p{Script=Tibetan}|\p{Script=Myanmar}/u,
    sym: /$^/u
};

const detectSlot = (text: string): Slot => {
    const counts: Record<Slot, number> = { latin: 0, ea: 0, cs: 0, sym: 0 };

    for (const character of text) {
        if (SCRIPT_REGEXES.ea.test(character)) counts.ea++;
        else if (SCRIPT_REGEXES.cs.test(character)) counts.cs++;
        else if (SCRIPT_REGEXES.latin.test(character)) counts.latin++;
        //Common/Inherited/Emoji — do nothing
    }

    const [slot, max] = Object.entries(counts).reduce((best, cur) => (cur[1] > best[1] ? cur : best), ['latin', 0] as [
        Slot,
        number
    ]);

    return max > 0 ? (slot as Slot) : 'latin';
};

const parseTypeface = (rawTypeface?: string): { family: string; style: string } => {
    const input = (rawTypeface || '').trim();
    if (!input) return { family: '', style: 'Regular' };

    const STYLE_PATTERNS = [
        'Extra Black',
        'Ultra Black',
        'Black Italic',
        'Extra Bold',
        'Ultra Bold',
        'Bold Italic',
        'Semi Bold',
        'Demi Bold',
        'Extra Light',
        'Ultra Light',
        'Medium Italic',
        'Light Italic',
        'Condensed Italic',
        'Condensed',
        'Oblique',
        'Italic',
        'Italics',
        'Black',
        'Bold',
        'Medium',
        'Light',
        'Regular',
        'Book',
        'Normal'
    ];
    const match = STYLE_PATTERNS.find((style) => new RegExp(`\\b${style.replace(/ /g, '\\s+')}\\s*$`, 'i').test(input));
    if (!match) return { family: input, style: 'Regular' };

    const family = input.replace(new RegExp(`\\s*${match.replace(/ /g, '\\s+')}\\s*$`, 'i'), '').trim();
    return { family, style: match };
};

const chooseTypeface = (rPr: any, text: string): CanvaFont => {
    const slot = detectSlot(text);

    const rawTypeface =
        (slot === 'latin' && rPr?.latin?.['@_typeface']) ||
        (slot === 'ea' && (rPr?.ea?.['@_typeface'] || rPr?.latin?.['@_typeface'])) ||
        (slot === 'cs' && (rPr?.cs?._typeface || rPr?.latin?.['@_typeface'])) ||
        rPr?.latin?.['@_typeface'] ||
        rPr?.['@_typeface'];

    const { family, style } = parseTypeface(rawTypeface);

    return { family, style, rawTypeface };
};

const colord = useColor();

const detectTextAlignment = (align: TextAlign) => {
    switch (align) {
        case 'l':
            return 'left';
        case 'ctr':
            return 'center';
        case 'r':
            return 'right';
        case 'just':
            return 'justify';
    }
};

const getPictureDrawParams = (params: { imageWidth: number; imageHeight: number; srcRect: FillRect2 }) => {
    const left = Number(params.srcRect['@_l']) / 100_000;
    const top = Number(params.srcRect['@_t']) / 100_000;
    const right = Number(params.srcRect['@_r']) / 100_000;
    const bottom = Number(params.srcRect['@_b']) / 100_000;

    const croppedImageLeft = params.imageWidth * left;
    const croppedImageTop = params.imageHeight * top;
    const croppedImageWidth = params.imageWidth * (1 - left - right);
    const croppedImageHeight = params.imageHeight * (1 - top - bottom);
    return { sx: croppedImageLeft, sy: croppedImageTop, sw: croppedImageWidth, sh: croppedImageHeight };
};

const getImageDrawParams = (params: {
    imageWidth: number;
    imageHeight: number;
    boundingBoxWidth: number;
    boundingBoxHeight: number;
    fillRect: FillRect;
}) => {
    const left = Number(params.fillRect['@_l']) / 100_000;
    const top = Number(params.fillRect['@_t']) / 100_000;
    const right = Number(params.fillRect['@_r']) / 100_000;
    const bottom = Number(params.fillRect['@_b']) / 100_000;

    const cropLeft = Math.max(0, left);
    const cropTop = Math.max(0, top);
    const cropRight = Math.max(0, right);
    const cropBottom = Math.max(0, bottom);

    const croppedImageLeft = params.imageWidth * cropLeft;
    const croppedImageTop = params.imageHeight * cropTop;
    const croppedImageWidth = params.imageWidth * (1 - cropLeft - cropRight);
    const croppedImageHeight = params.imageHeight * (1 - cropTop - cropBottom);

    const bleedLeft = Math.max(0, -left);
    const bleedTop = Math.max(0, -top);
    const bleedRight = Math.max(0, -right);
    const bleedBottom = Math.max(0, -bottom);

    const dx = 0 - params.boundingBoxWidth * bleedLeft;
    const dy = 0 - params.boundingBoxHeight * bleedTop;
    const dw = params.boundingBoxWidth * (1 + bleedLeft + bleedRight);
    const dh = params.boundingBoxHeight * (1 + bleedTop + bleedBottom);

    return {
        sx: croppedImageLeft,
        sy: croppedImageTop,
        sWidth: croppedImageWidth,
        sHeight: croppedImageHeight,
        dx,
        dy,
        dw,
        dh
    };
};

const svgToBlob = (svg: string) => {
    const svgWithHeader = svg.startsWith('<?xml') ? svg : `<?xml version="1.0" encoding="UTF-8"?>\n${svg}`;

    return new Blob([svgWithHeader], { type: 'image/svg+xml' });
};

const renderCustGeomMaskSVG = (
    custGeom: CustGeom,
    bbox: LayerBoundingBoxProperties,
    orderedSeq: OperationItem[]
): Blob => {
    const { d, fillRule } = custGeomToSvgPathOrdered(custGeom, bbox, orderedSeq);

    const pxW = pptxUnitsToPixels(bbox.width);
    const pxH = pptxUnitsToPixels(bbox.height);

    const svgMask = `
<svg xmlns="http://www.w3.org/2000/svg" width="${pxW}" height="${pxH}" viewBox="0 0 ${pxW} ${pxH}">
  <rect x="0" y="0" width="${pxW}" height="${pxH}" fill="white"/>
  <path d="${d}"
        ${fillRule ? `fill-rule="${fillRule}"` : ''}
        fill="black"
       />
</svg>`.trim();

    return svgToBlob(svgMask);
};

export const parseLayerNode = async (
    layerNode: LayerNode,
    relationships: Record<string, string>,
    zipReader: ZipReader<Blob>,
    orderedTree: OrderedSlide.RootTree[]
): Promise<LayerElementsMetadata[]> => {
    let elements: LayerElementsMetadata[] = [];
    if (Array.isArray(layerNode)) {
        for (const node of layerNode) {
            const nodeElements = await parseLayerNode(node, relationships, zipReader, orderedTree);
            elements = [...elements, ...nodeElements];
        }
        return elements;
    }

    if ('grpSp' in layerNode && layerNode.grpSp) {
        const elementsFromGroup = await parseLayerNode(layerNode.grpSp, relationships, zipReader, orderedTree);
        elements = [...elements, ...elementsFromGroup];
    }

    if ('pic' in layerNode && layerNode.pic) {
        const pictures: Pic[] = Array.isArray(layerNode.pic) ? layerNode.pic : [layerNode.pic];
        for (const picture of pictures) {
            const layerName = picture.nvPicPr.cNvPr['@_name'];
            const zIndex = picture.nvPicPr.cNvPr['@_id'] ? Number(picture.nvPicPr.cNvPr['@_id']) : undefined;
            const boundingBox = getFullBoundingBoxProperties(picture.spPr.xfrm, layerNode);
            const embeddedImageId = picture.blipFill.blip['@_embed'];
            const imageSrc = await loadImageData(embeddedImageId, relationships, zipReader);
            const imageElement: LayerElementsMetadata = {
                name: layerName,
                left: boundingBox.left,
                top: boundingBox.top,
                width: boundingBox.width,
                height: boundingBox.height,
                rotation: boundingBox.rotation,
                flipX: boundingBox.flipX,
                flipY: boundingBox.flipY,
                zIndex,
                visible: true,
                imgSrc: imageSrc,
                type: TreeType.FROM_FILE_IMAGE_ELEMENT
            };

            const { blipFill } = picture;
            if (blipFill.srcRect) {
                const boundingBoxWidth = pptxUnitsToPixels(boundingBox.width);
                const boundingBoxHeight = pptxUnitsToPixels(boundingBox.height);
                const url = URL.createObjectURL(imageSrc);
                const image = await getLoadedHtmlImage(url);
                const resizedBlob = await applySrcRectToPicture(
                    image,
                    boundingBoxWidth,
                    boundingBoxHeight,
                    blipFill.srcRect
                );
                imageElement.imgSrc = resizedBlob;
                URL.revokeObjectURL(url);
            }
            elements.push(imageElement);
        }
    }

    if ('sp' in layerNode && layerNode.sp) {
        const shapeElements = Array.isArray(layerNode.sp) ? layerNode.sp : [layerNode.sp];
        for (const element of shapeElements) {
            const isImage = !!element.spPr?.blipFill;
            const layerName = element.nvSpPr.cNvPr['@_name'];
            const zIndex = element.nvSpPr.cNvPr['@_id'] ? Number(element.nvSpPr.cNvPr['@_id']) : undefined;
            const boundingBox = getFullBoundingBoxProperties(element.spPr.xfrm, layerNode);

            let baseElement: LayerElementsMetadata = {
                name: layerName,
                left: boundingBox.left,
                top: boundingBox.top,
                width: boundingBox.width,
                height: boundingBox.height,
                rotation: boundingBox.rotation,
                flipX: boundingBox.flipX,
                flipY: boundingBox.flipY,
                zIndex,
                visible: true,
                type: isImage ? TreeType.FROM_FILE_IMAGE_ELEMENT : TreeType.FROM_FILE_TEXT_ELEMENT
            };

            if (element.spPr?.blipFill) {
                const imageBlob = await getImageElement(element, relationships, zipReader);
                if (!imageBlob) continue;
                baseElement.imgSrc = imageBlob;
                baseElement.name = `Image ${baseElement.zIndex}`;

                const { custGeom, blipFill } = element.spPr;
                if (custGeom) {
                    const orderedPathProperties = buildCustGeomSvgPathProperties(element, orderedTree);
                    const maskBlob = renderCustGeomMaskSVG(custGeom, boundingBox, orderedPathProperties);
                    baseElement.maskSrc = maskBlob;
                }

                if (blipFill.stretch?.fillRect) {
                    const boundingBoxWidth = pptxUnitsToPixels(boundingBox.width);
                    const boundingBoxHeight = pptxUnitsToPixels(boundingBox.height);
                    const { fillRect } = blipFill.stretch;
                    const url = URL.createObjectURL(imageBlob);
                    const image = await getLoadedHtmlImage(url);
                    const resizedBlob = await applyFillRectToImage(
                        image,
                        boundingBoxWidth,
                        boundingBoxHeight,
                        fillRect
                    );
                    baseElement.imgSrc = resizedBlob;
                    URL.revokeObjectURL(url);
                }

                elements.push(baseElement);
            } else if (element.spPr.solidFill || element.spPr.custGeom) {
                const color = element.spPr.solidFill?.srgbClr?.['@_val'];
                const alpha = Number(element.spPr.solidFill?.srgbClr?.alpha?.['@_val'] || 1);
                const { custGeom } = element.spPr;
                if (!color?.length && !custGeom) continue;
                if (color?.length) {
                    const { cmyk, hex, a } = colord.getColorFromString(colord.hexToHexa(`#${color}`, alpha));
                    const colorFill: ColorObject = {
                        hex,
                        alpha: a,
                        cmyk: colord.cmykToDecimalString(cmyk)
                    };
                    baseElement.colorFill = colorFill;
                    // solid background
                    baseElement.type = TreeType.FROM_FILE_VECTOR_PLACEHOLDER_ELEMENT;
                }
                if (custGeom) {
                    const orderedPathProperties = buildCustGeomSvgPathProperties(element, orderedTree);

                    const { d, fillRule } = custGeomToSvgPathOrdered(custGeom, boundingBox, orderedPathProperties);

                    let fill = colord.hexToHexa(baseElement.colorFill?.hex || '#000000', alpha);
                    let defs = '';
                    if (element.spPr.gradFill) {
                        const id = `g_${zIndex}`;
                        const { defs: gradientDefs, fill: gradientFill } = getCustGeomLinearGradient(
                            id,
                            element.spPr.gradFill
                        );
                        defs = gradientDefs;
                        fill = gradientFill;
                    }
                    const svg = `
                        <svg xmlns="http://www.w3.org/2000/svg" width="${pptxUnitsToPixels(
                            baseElement.width
                        )}" height="${pptxUnitsToPixels(baseElement.height)}">
                        ${defs}
                            <path d="${d}" ${fillRule ? `fill-rule="${fillRule}"` : ''} fill="${fill}" />
                        </svg>`;
                    baseElement.svgString = svg;
                    baseElement.colorFill = undefined;
                    baseElement.type = TreeType.FROM_FILE_VECTOR_ELEMENT;
                }
                baseElement.name = `Vector ${baseElement.zIndex}`;
                elements.push(baseElement);
            } else {
                const textElement = getTextElement(element);
                if (!textElement) continue;
                const textName = textElement.textValue?.substring(0, 35);
                baseElement = { ...baseElement, ...textElement, name: textName || '' };
                elements.push(baseElement);
            }
        }
    }

    return elements;
};

const applyFillRectToImage = async (
    image: HTMLImageElement,
    boundingBoxWidth: number,
    boundingBoxHeight: number,
    fillRect: FillRect
) => {
    const params = {
        imageWidth: image.width,
        imageHeight: image.height,
        boundingBoxWidth,
        boundingBoxHeight,
        fillRect
    };
    const draw = getImageDrawParams(params);

    if (draw.sWidth <= 0 || draw.sHeight <= 0) return;

    const finalCanvas = createCanvas(
        Math.max(1, Math.ceil(boundingBoxWidth)),
        Math.max(1, Math.ceil(boundingBoxHeight))
    );
    const offscreenCanvasContext = finalCanvas.getContext('2d')!;
    offscreenCanvasContext.clearRect(0, 0, finalCanvas.width, finalCanvas.height);

    offscreenCanvasContext.drawImage(
        image,
        draw.sx,
        draw.sy,
        draw.sWidth,
        draw.sHeight,
        draw.dx,
        draw.dy,
        draw.dw,
        draw.dh
    );

    const blob = await canvasToBlob(finalCanvas);
    return blob;
};

const applySrcRectToPicture = async (
    image: HTMLImageElement,
    boundingBoxWidth: number,
    boundingBoxHeight: number,
    srcRect: FillRect
) => {
    const params = {
        imageWidth: image.width,
        imageHeight: image.height,
        srcRect
    };
    const draw = getPictureDrawParams(params);
    const finalCanvas = createCanvas(
        Math.max(1, Math.ceil(boundingBoxWidth)),
        Math.max(1, Math.ceil(boundingBoxHeight))
    );
    const offscreenCanvasContext = finalCanvas.getContext('2d')!;
    offscreenCanvasContext.clearRect(0, 0, finalCanvas.width, finalCanvas.height);

    offscreenCanvasContext.drawImage(
        image,
        draw.sx,
        draw.sy,
        draw.sw,
        draw.sh,
        0,
        0,
        boundingBoxWidth,
        boundingBoxHeight
    );

    const blob = await canvasToBlob(finalCanvas);
    return blob;
};

export const getImageElement = async (
    imageNode: Sp | Sp2,
    relationships: Record<string, string>,
    zipReader: ZipReader<Blob>
): Promise<Blob | null> => {
    const imageFill = imageNode.spPr?.blipFill;
    if (!(imageFill && imageFill.blip)) return null;
    const embeddedImageId = imageFill.blip['@_embed'];
    const imageSrc = await loadImageData(embeddedImageId, relationships, zipReader);
    return imageSrc;
};

export const getTextElement = (textNode: Sp | Sp2): Partial<LayerElementsMetadata> | null => {
    const textBody = textNode.txBody;
    if (!textBody) return null;
    const textLines: TextLine[] = Array.isArray(textBody.p) ? textBody.p : [textBody.p];
    const text = textLines
        .map((line: TextLine) => {
            if (line.r) {
                const textRuns = Array.isArray(line.r) ? line.r : [line.r];
                return textRuns.map((run: R) => run.t || '').join('');
            } else if (line.t) {
                return line.t;
            }
            return '';
        })
        .join('\n');

    if (!text.trim()) return null;
    if (!textLines.length) return null;

    const align = textLines[0].pPr['@_algn'] as TextAlign;
    const textAlign = detectTextAlignment(align);

    const textData: Partial<LayerElementsMetadata> = {
        textAlign,
        textValue: text,
        multiline: true
    };

    const firstLineWithText = textLines.filter((line: TextLine) => line.r);
    if (!firstLineWithText.length) return textData;
    //if in the same line of text, we have words with different configuration (for example, the last word of the sentence has another color fill, 'r' object will be an array)
    const lineWords: R = Array.isArray(firstLineWithText[0]!.r) ? firstLineWithText[0]!.r[0] : firstLineWithText[0]!.r;
    const font = chooseTypeface(lineWords.rPr, text);
    textData.font = font;

    const lineHeight = firstLineWithText[0].pPr['lnSpc'].spcPts['@_val'];
    if (lineWords.rPr['@_sz'] && lineHeight) {
        textData.fontSize = Number(lineWords.rPr['@_sz']);
        const lineSpacing = ((Number(lineHeight) / textData.fontSize) * pptxToFabricLineCorrection).toFixed(2);
        textData.lineSpacing = Number(lineSpacing);
    }

    const color = lineWords.rPr.solidFill?.srgbClr['@_val'];
    if (!color.length) return textData;
    const { cmyk, hex, a } = colord.getColorFromString(`#${color}`);
    const colorFill: ColorObject = {
        hex,
        alpha: a,
        cmyk: colord.cmykToDecimalString(cmyk)
    };
    textData.colorFill = colorFill;
    return textData;
};

export const getBoundingBoxProperties = (dimensionsObject: Xfrm2) => {
    const off = dimensionsObject.off;
    const ext = dimensionsObject.ext;
    const rot = dimensionsObject['@_rot'];

    return {
        left: Number(off?.['@_x'] || 0),
        top: Number(off?.['@_y'] || 0),
        width: Number(ext?.['@_cx'] || 0),
        height: Number(ext?.['@_cy'] || 0),
        flipX: dimensionsObject['@_flipH'] === 'true',
        flipY: dimensionsObject['@_flipV'] === 'true',
        rotation: rot?.length ? Number(rot) / 60000 : 0
    };
};

export const getFullBoundingBoxProperties = (
    childDimensions: Xfrm2,
    parentNodeGroup: SpTree | GrpSp2
): LayerBoundingBoxProperties => {
    const childBoundingBox = getBoundingBoxProperties(childDimensions);
    const parentBox = getBoundingBoxProperties(parentNodeGroup.grpSpPr.xfrm);
    const { xfrm: groupTransform } = parentNodeGroup.grpSpPr;
    const groupOriginLeft = Number(groupTransform.chOff['@_x']);
    const groupOriginTop = Number(groupTransform.chOff['@_y']);
    const groupAvailableWidth = Number(groupTransform.chExt['@_cx']);
    const groupAvailableHeight = Number(groupTransform.chExt['@_cy']);
    const parentRotation = Number(groupTransform['@_rot'] || 0) / 60000;

    if (!groupAvailableWidth && !groupAvailableHeight) return childBoundingBox;
    const scaleX = parentBox.width / groupAvailableWidth;
    const scaleY = parentBox.height / groupAvailableHeight;
    const parentFlipX = groupTransform['@_flipH'] === 'true';
    const parentFlipY = groupTransform['@_flipV'] === 'true';

    return {
        left: parentBox.left + (childBoundingBox.left - groupOriginLeft) * scaleX,
        top: parentBox.top + (childBoundingBox.top - groupOriginTop) * scaleY,
        width: childBoundingBox.width * scaleX,
        height: childBoundingBox.height * scaleY,
        flipX: childBoundingBox.flipX ? childBoundingBox.flipX !== parentFlipX : childBoundingBox.flipX,
        flipY: childBoundingBox.flipY ? childBoundingBox.flipY !== parentFlipY : childBoundingBox.flipY,
        rotation: childBoundingBox.rotation + parentRotation
    };
};

export const getPageDimensions = (page: Record<'@_cx' | '@_cy', string>): { width: number; height: number } => {
    let dimensions = { width: 0, height: 0 };
    if (!page) return dimensions;
    dimensions.width = pptxUnitsToPixels(Number(page?.['@_cx'] || 0));
    dimensions.height = pptxUnitsToPixels(Number(page?.['@_cy'] || 0));
    return dimensions;
};

export const loadImageData = async (
    embedId: string,
    relationships: Record<string, string>,
    zipReader: ZipReader<BlobWriter>
): Promise<Blob> => {
    const target = relationships[embedId];
    if (!target) return new Blob();

    const imagePath = target.replace('..', 'ppt');
    const entries = await zipReader.getEntries();
    const imageEntry = entries.find((e: any) => e.filename === imagePath);
    if (!imageEntry?.getData) throw new Error('failed to load the image');

    const imageBlob = await imageEntry.getData(new BlobWriter('image/png'));
    return imageBlob;
};

export const getFileText = async (zipReader: any, filename: string): Promise<string> => {
    const entries = await zipReader.getEntries();
    const entry = entries.find((e: any) => e.filename === filename);
    if (!entry) throw new Error(`${filename} not found in PPTX`);
    return await entry.getData(new TextWriter());
};

const getCustGeomLinearGradient = (shapeId: string, gradFill: GradFill) => {
    const angle = Number(gradFill.lin?.['@_ang'] ?? 0);
    const deg = angle / 60000;

    const stops = (Array.isArray(gradFill.gsLst.gs) ? gradFill.gsLst.gs : [gradFill.gsLst.gs])
        .sort((a, b) => Number(a['@_pos'] ?? 0) - Number(b['@_pos'] ?? 0))
        .map((s) => {
            const offset = (Number(s['@_pos'] ?? 0) / 100000).toFixed(4);
            const color = `#${s.srgbClr?.['@_val'] ?? '000000'}`;
            const alpha = s.srgbClr?.alpha ? Number(s.srgbClr.alpha['@_val']) / 100000 : 1;
            return `<stop offset="${offset}" stop-color="${color}" stop-opacity="${alpha}"/>`;
        })
        .join('');

    const gradientUnits = Boolean(gradFill['@_rotWithShape']) ? 'objectBoundingBox' : 'userSpaceOnUse';
    const gradTransform = `rotate(${deg}, 0.5, 0.5)`;

    const defs = `<defs>
                    <linearGradient id="${shapeId}"
                        gradientUnits="${gradientUnits}"
                        x1="0" y1="0.5" x2="1" y2="0.5"
                        gradientTransform="${gradTransform}">
                        ${stops}
                    </linearGradient>
                </defs>`;

    const fill = `url(#${shapeId})`;
    return { defs, fill };
};

const custGeomToSvgPathOrdered = (
    custGeom: CustGeom | CustGeom2,
    boundingBox: LayerBoundingBoxProperties,
    orderedSeq: OperationItem[]
) => {
    const pathNodeRaw = custGeom.pathLst.path;
    const pathNode = Array.isArray(pathNodeRaw) ? pathNodeRaw[0] : pathNodeRaw;
    if (!pathNode) throw new Error('No a:path element in custGeom');

    const pathWidth = Number(pathNode['@_w']) || 1;
    const pathHeight = Number(pathNode['@_h']) || 1;

    const { width, height, rotation, flipX, flipY } = boundingBox;
    const scaleX = width / pathWidth;
    const scaleY = height / pathHeight;

    const convertPathDimensionsToCanvas = (x: number, y: number) => ({
        X: pptxUnitsToPixels(x * scaleX),
        Y: pptxUnitsToPixels(y * scaleY)
    });

    const operations: string[] = [];
    let subpathCount = 0;

    for (const s of orderedSeq) {
        switch (s.type) {
            case 'attrs':
                break;

            case 'moveTo': {
                const pt = s.item?.pt;
                if (!pt) break;
                const moveTo = convertPathDimensionsToCanvas(Number(pt['@_x']), Number(pt['@_y']));
                operations.push(`M ${moveTo.X} ${moveTo.Y}`);
                subpathCount++;
                break;
            }

            case 'lnTo': {
                const pt = s.item?.pt;
                if (!pt) break;
                const lnTo = convertPathDimensionsToCanvas(Number(pt['@_x']), Number(pt['@_y']));
                operations.push(`L ${lnTo.X} ${lnTo.Y}`);
                break;
            }

            case 'cubicBezTo': {
                const triplet = Array.isArray(s.item?.pt) ? s.item.pt : [s.item?.pt];
                const [c1, c2, e] = triplet || [];
                if (!c1 || !c2 || !e) break;
                const C1 = convertPathDimensionsToCanvas(Number(c1['@_x']), Number(c1['@_y']));
                const C2 = convertPathDimensionsToCanvas(Number(c2['@_x']), Number(c2['@_y']));
                const E = convertPathDimensionsToCanvas(Number(e['@_x']), Number(e['@_y']));
                operations.push(`C ${C1.X} ${C1.Y} ${C2.X} ${C2.Y} ${E.X} ${E.Y}`);
                break;
            }

            case 'close':
                operations.push('Z');
                break;
        }
    }

    const fillRule = subpathCount > 1 ? 'evenodd' : undefined;

    return { d: operations.join(' '), fillRule };
};

const findShapeInOrderedTreeByShapeId = (
    orderedSlide: OrderedSlide.RootTree[],
    shapeId: string
): OrderedSlide.GrpSp | undefined => {
    const findShape = (
        nodes:
            | OrderedSlide.RootTree[]
            | OrderedSlide.Sld[]
            | OrderedSlide.CSld[]
            | OrderedSlide.SpTree[]
            | OrderedSlide.GrpSp[]
    ): OrderedSlide.GrpSp | undefined => {
        if (!Array.isArray(nodes)) return undefined;

        for (const node of nodes) {
            const [key, value] = Object.entries(node)[0] || [];
            if (key === 'sp' && value.length) {
                // Look for the shapeId, properties are stored as an array of objects
                const shapeProperties: OrderedSlide.Sp[] = value;
                const nvSpPrEntry = shapeProperties.find((obj) => 'nvSpPr' in obj);
                if (nvSpPrEntry?.nvSpPr?.length) {
                    const cNvPr = nvSpPrEntry.nvSpPr.find((x: OrderedSlide.NvSpPr) => ':@' in x);
                    const idFound = cNvPr?.[':@']?.['@_id'];
                    if (String(idFound) === shapeId) {
                        return node as OrderedSlide.GrpSp;
                    }
                }
            }

            if (['sld', 'cSld', 'spTree', 'grpSp'].includes(key) && value.length) {
                const result = findShape(value);
                if (result) return result;
            }
        }

        return undefined;
    };

    return findShape(orderedSlide);
};

/**
 * Our current implementation uses 'XMLParser with preserveOrder: false', thus the custGeomPathList comes as an object of
 * moveTo/lnTo/cubicBezTo/close arrays, losing the original order.
 *
 * This function reconstructs the original order by cross-referencing with a 'preserveOrder: true' parsed tree.
 * @param `shape` is the current shape.
 * @param `orderedSlide` is the preserveOrder=true tree.
 *
 * @returns `SeqItem[]` The returned array contains objects like:
 *   { type: 'moveTo', item, attrs? }
 *   { type: 'lnTo', item }
 *   { type: 'cubicBezTo', item }
 *   { type: 'quadBezTo', item }
 *   { type: 'close' }
 */
const buildCustGeomSvgPathProperties = (shape: Sp | Sp2, orderedSlide: OrderedSlide.RootTree[]): OperationItem[] => {
    const shapeId = shape.nvSpPr.cNvPr['@_id'];
    const spOrdered = findShapeInOrderedTreeByShapeId(orderedSlide, shapeId);
    if (!spOrdered?.sp) throw new Error('Shape not found in the ordered tree');

    const path = shape.spPr.custGeom?.pathLst?.path;
    if (!path) throw new Error('No path found in custGeom');
    const pools = {
        moveTo: Array.isArray(path.moveTo) ? path.moveTo : [path.moveTo],
        lnTo: Array.isArray(path.lnTo) ? path.lnTo : [path.lnTo],
        cubicBezTo: Array.isArray(path.cubicBezTo) ? path.cubicBezTo : [path.cubicBezTo],
        quadBezTo: Array.isArray(path.quadBezTo) ? path.quadBezTo : [path.quadBezTo],
        close: Array.isArray(path.close) ? path.close : [path.close]
    };

    /** Returns the array under `key` from a preserve-order node. */
    const childOf = <T>(node: unknown[] | Record<string, unknown> | null | undefined, key: string): T[] => {
        if (Array.isArray(node)) {
            const hit = node.find(
                (nodeObject: unknown) =>
                    !!nodeObject &&
                    typeof nodeObject === 'object' &&
                    !Array.isArray(nodeObject) &&
                    key in (nodeObject as Record<string, unknown>)
            ) as Record<string, unknown> | undefined;
            return (hit?.[key] as T[]) ?? [];
        }
        if (node && typeof node === 'object') {
            return ((node as Record<string, unknown>)[key] as T[]) ?? [];
        }
        return [];
    };

    const spPrArr = childOf<OrderedSlide.SpPr2>(spOrdered.sp, 'spPr');
    const custGeomArr = childOf<OrderedSlide.CustGeom>(spPrArr, 'custGeom');
    const pathLstArr = childOf<OrderedSlide.PathLst>(custGeomArr, 'pathLst');
    const orderedPathSequence = childOf<OrderedSlide.Path>(pathLstArr, 'path');
    if (!orderedPathSequence.length) throw new Error('Ordered <a:path> not found');

    const operationSequence: OperationItem[] = [];

    for (const child of orderedPathSequence) {
        if (child[':@' as keyof OrderedPathChild]) {
            continue;
        }
        if ('moveTo' in child && child.moveTo) {
            const item = pools.moveTo.shift();
            operationSequence.push({ type: 'moveTo', item });
        } else if ('lnTo' in child && child.lnTo) {
            const item = pools.lnTo.shift();
            operationSequence.push({ type: 'lnTo', item });
        } else if ('cubicBezTo' in child && child.cubicBezTo) {
            const item = pools.cubicBezTo.shift();
            operationSequence.push({ type: 'cubicBezTo', item });
        } else if ('close' in child && child.close) {
            pools.close.shift();
            operationSequence.push({ type: 'close' });
        }
    }

    return operationSequence;
};
